import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminGroupService } from '../../services/groupService';
import { GroupStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TABS = ['All Groups', 'Pending Payouts', 'Short Cycles'];

function AllGroupsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-groups'],
    queryFn: () => adminGroupService.getGroups().then(r => r.data.data),
  });
  const groups = data?.results || [];

  if (isLoading) return <LoadingSpinner />;
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Reference</th><th>Name</th><th>Organizer</th><th>Contribution</th><th>Cycle</th><th>Members</th><th>Status</th></tr></thead>
          <tbody>
            {groups.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No groups yet.</td></tr>
            ) : groups.map(g => (
              <tr key={g.id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{g.reference}</td>
                <td style={{ fontWeight: 600 }}>{g.name}</td>
                <td>{g.organizer_name}</td>
                <td>{formatCurrency(g.contribution_amount)}</td>
                <td style={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>{g.cycle}</td>
                <td>{g.member_count}/{g.max_members}</td>
                <td><GroupStatusBadge status={g.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PendingPayoutsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['pending-group-payouts'],
    queryFn: () => adminGroupService.getPendingPayouts().then(r => r.data.data),
  });
  const payouts = data?.results || [];

  const executeMutation = useMutation({
    mutationFn: (id) => adminGroupService.executePayout(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-group-payouts'] }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (payouts.length === 0) return <div className="card"><p style={{ color: 'var(--text-3)', textAlign: 'center', padding: '1rem' }}>No pending payouts.</p></div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Reference</th><th>Recipient</th><th>Amount</th><th>Shortfall</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {payouts.map(p => (
              <tr key={p.id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.reference}</td>
                <td>{p.recipient_name}</td>
                <td style={{ fontWeight: 700, color: '#00d9a5' }}>{formatCurrency(p.amount)}</td>
                <td style={{ color: parseFloat(p.shortfall_amount) > 0 ? '#F5A623' : 'var(--text-3)' }}>{parseFloat(p.shortfall_amount) > 0 ? formatCurrency(p.shortfall_amount) : '—'}</td>
                <td><GroupStatusBadge status={p.status} /></td>
                <td>
                  <button
                    className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                    disabled={executeMutation.isPending}
                    onClick={() => executeMutation.mutate(p.id)}
                  >
                    Execute
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: 'var(--text-3)' }}>
        Payouts above the auto-release ceiling or flagged for fraud review require approval in the Approval Queue before they can be executed.
      </p>
    </div>
  );
}

function ShortCyclesTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['short-group-cycles'],
    queryFn: () => adminGroupService.getShortCycles().then(r => r.data.data),
  });
  const cycles = data?.results || [];

  const resolveMutation = useMutation({
    mutationFn: (cycleId) => adminGroupService.resolveShortfall(cycleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['short-group-cycles'] }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (cycles.length === 0) return <div className="card"><p style={{ color: 'var(--text-3)', textAlign: 'center', padding: '1rem' }}>No short cycles.</p></div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Cycle</th><th>Recipient</th><th>Due</th><th>Collected</th><th>Expected</th><th></th></tr></thead>
          <tbody>
            {cycles.map(c => (
              <tr key={c.id}>
                <td>#{c.cycle_number}</td>
                <td>{c.recipient_name}</td>
                <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(c.due_date)}</td>
                <td style={{ color: '#F5A623' }}>{formatCurrency(c.collected_amount)}</td>
                <td>{formatCurrency(c.expected_pot_amount)}</td>
                <td>
                  <button
                    className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                    disabled={resolveMutation.isPending}
                    onClick={() => resolveMutation.mutate(c.id)}
                  >
                    Re-evaluate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function GroupsAdmin() {
  const [tab, setTab] = useState(0);
  return (
    <div>
      <div className="page-header">
        <h1>Groups & Payouts</h1>
        <p>Rotating group contributions (Ajo) — oversight, payout release, and dispute resolution</p>
      </div>
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', background: 'var(--surface)', borderRadius: 12, padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '0.5rem 1.1rem', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: tab === i ? 700 : 400,
            background: tab === i ? 'linear-gradient(135deg, rgba(21,150,224,0.2), rgba(21,150,224,0.08))' : 'transparent',
            color: tab === i ? '#1596E0' : 'var(--text-2)',
            transition: 'all .15s',
          }}>
            {t}
          </button>
        ))}
      </div>
      {tab === 0 ? <AllGroupsTab /> : tab === 1 ? <PendingPayoutsTab /> : <ShortCyclesTab />}
    </div>
  );
}
